/**
 * nav_overflow_controller.js — desktop navbar priority overflow.
 *
 * On the desktop navbar the centre links (…Calendar, Map, admin sections) can grow
 * wider than the row and push the right-hand context dropdown off-screen on
 * narrow desktop windows. This controller measures the collapse row and folds
 * the lowest-priority links into a compact "More" dropdown until the row fits,
 * restoring them as width returns. It is width-driven, so it adapts to the
 * variable business-name width in the context button instead of guessing a
 * fixed breakpoint.
 *
 * Markup contract (templates/components/navbar.html):
 *   - Controller element: the centre <ul class="navbar-nav mx-auto">.
 *   - Collapsible inline links: <li class="nav-overflow-item" data-overflow-key="…"
 *     data-overflow-priority="N"> — lower N folds away first (Map=1, Calendar=2).
 *   - "More" dropdown: <li class="nav-overflow-more"> holding one
 *     <li class="nav-overflow-collapsed" data-overflow-key="…"> per collapsible
 *     link. The controller toggles `d-none` on each inline/collapsed pair.
 *   - Optional: data-overflow-persistent="true" on .nav-overflow-more keeps the
 *     dropdown visible even when nothing is folded, for navs with permanent
 *     dropdown-only links.
 *
 * Turbo-safe: connect() wires a ResizeObserver on the collapse row; disconnect()
 * tears it down, so it cannot leak across Turbo body swaps. The observed element
 * (#navbarNav) is full-row width, so toggling child links never changes its size
 * and the observer cannot self-trigger a loop.
 */

import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
        connect() {
            // Measure against the Bootstrap .container, NOT .navbar-collapse. The
            // collapse is a flex item with flex-grow and no max-width, so it simply
            // grows past the container when its links don't fit — its scrollWidth
            // then equals its clientWidth and overflow is never detected. The
            // .container is width-capped per breakpoint, so its clientWidth stays
            // fixed while overflowing content shows up in its scrollWidth.
            this.container = this.element.closest('.container');
            if (!this.container) return;

            // Inline collapsible items, ordered by collapse priority (low folds first).
            this.items = Array.from(this.element.querySelectorAll('.nav-overflow-item'))
                .sort(function (a, b) {
                    return Number(a.dataset.overflowPriority) - Number(b.dataset.overflowPriority);
                });
            this.moreLi = this.element.querySelector('.nav-overflow-more');
            if (!this.items.length || !this.moreLi) return; // nothing to fold

            // Collapsed dropdown counterparts keyed by data-overflow-key.
            this.collapsed = {};
            this.moreLi.querySelectorAll('.nav-overflow-collapsed').forEach((li) => {
                this.collapsed[li.dataset.overflowKey] = li;
            });
            this.morePersistent = this.moreLi.dataset.overflowPersistent === 'true';

            this._reflow = this.reflow.bind(this);
            this.observer = new ResizeObserver(this._reflow);
            this.observer.observe(this.container);
            // The container only changes size at breakpoints, so also reflow on any
            // window resize to catch within-breakpoint width changes (e.g. a browser
            // sidebar opening, which narrows the viewport but not the lg container).
            window.addEventListener('resize', this._reflow);
            this.reflow();
        }

        disconnect() {
            if (this.observer) {
                this.observer.disconnect();
                this.observer = null;
            }
            if (this._reflow) window.removeEventListener('resize', this._reflow);
        }

        isOverflowing() {
            return this.container.scrollWidth > this.container.clientWidth + 1;
        }

        reflow() {
            // Reset to fully expanded.
            this.items.forEach((li) => li.classList.remove('d-none'));
            Object.keys(this.collapsed).forEach((key) => this.collapsed[key].classList.add('d-none'));
            this.moreLi.classList.toggle('d-none', !this.morePersistent);
            const toggle = this.moreLi.querySelector('.nav-link');
            if (toggle) toggle.classList.remove('active');

            // Navbar hidden (mobile breakpoint) or the row already fits: nothing to fold.
            if (this.element.offsetParent === null || !this.isOverflowing()) return;

            for (const li of this.items) {
                const key = li.dataset.overflowKey;
                li.classList.add('d-none');
                if (this.collapsed[key]) this.collapsed[key].classList.remove('d-none');
                this.moreLi.classList.remove('d-none');
                if (!this.isOverflowing()) break;
            }

            this.syncMoreActive(toggle);
        }

        // Mirror the active-page indicator onto the "More" trigger when the
        // current page's link has been folded into the dropdown, so the user
        // doesn't lose the "you are here" cue.
        syncMoreActive(toggle) {
            if (!toggle) return;
            const path = window.location.pathname;
            for (const key in this.collapsed) {
                const li = this.collapsed[key];
                if (li.classList.contains('d-none')) continue;
                const link = li.querySelector('a[href]');
                const href = link && link.getAttribute('href');
                if (href && href !== '/' && path.startsWith(href)) {
                    toggle.classList.add('active');
                    return;
                }
            }
        }
}
